import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { Back, CloseCircle } from 'iconsax-react'
import { IconButton } from '@mui/material'

import { AnimatePresence, motion } from 'framer-motion'
import { useNoteVisibility } from '@SonarCX/providers/NoteVisibilityProvider'
import useSavedValue from '@SonarCX/hooks/useSavedValue'
import useStyles from './styles'

interface SideModalProps {
  title: string
  sidemodalKey: string
  onClose: () => void
  onBack?: () => void
  rootClass?: string
  minWidth?: number
  draggable?: boolean
  children: React.ReactNode
}

const SideModal: React.FC<SideModalProps> = ({
  title,
  sidemodalKey,
  onClose,
  onBack,
  draggable,
  rootClass = undefined,
  minWidth = 289,
  children
}) => {
  const { setModalWidth } = useNoteVisibility()
  const [width, setWidth] = useSavedValue({
    key: sidemodalKey,
    defaultValue: minWidth.toString(),
    useSession: true
  })
  const [resizing, setResizing] = useState(false)
  const [startX, setStartX] = useState(0)
  const classes = useStyles({ minWidth })

  const handleMouseDown = e => {
    setResizing(true)
    setStartX(e.clientX)
  }

  useEffect(() => {
    const handleMouseMove = e => {
      if (!resizing) return

      const delta = startX - e.clientX
      setWidth(prevWidth =>
        prevWidth + delta < minWidth ? minWidth : prevWidth + delta
      )
      setStartX(e.clientX)
    }

    const handleMouseUp = () => {
      if (Number(width) < minWidth) {
        setWidth(minWidth)
      }
      setResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizing, startX])

  useEffect(() => {
    setModalWidth(Number(width))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width])

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: '100%' }}
        animate={{
          x: 0
        }}
        exit={{
          x: '100%'
        }}
        transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
      >
        <div
          className={clsx(classes.root, rootClass)}
          style={{
            width: `${width}px`,
            userSelect: resizing ? 'none' : 'initial',
            cursor: resizing ? 'ew-resize' : 'initial'
          }}
        >
          {draggable && (
            <div className='crop-left-line' onMouseDown={handleMouseDown}></div>
          )}
          <IconButton
            className={classes.close}
            onClick={onClose}
            data-testid={'filter-close-icon'}
          >
            <CloseCircle />
          </IconButton>

          {onBack && (
            <IconButton
              className={classes.back}
              onClick={onBack}
              data-testid={'filter-back-icon'}
            >
              <Back />
            </IconButton>
          )}
          <div className={classes.title}>{title}</div>

          <div className={classes.children}>{children}</div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default SideModal
